import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconCircleCheck, IconCircleX, IconExclamationCircle } from '@tabler/icons-react'

import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import Select from '@/components/forms/Select'
import { TextArea } from '@/components/forms/TextArea'
import { TextInput } from '@/components/forms/TextInput'
import { testPrompt } from '@/reducers/playbooks/playbook.actions'

import './PromptTester.scss'

const MODES = { CALL_ID: 'call_id', RAW_TEXT: 'raw_text' }
const SIDES = { AGENT: 'agent', CUSTOMER: 'customer' }

const SIDE_OPTIONS = [
  { value: SIDES.AGENT, label: 'Agent' },
  { value: SIDES.CUSTOMER, label: 'Customer' },
]

export const PromptTester = ({ prompt }) => {
  const dispatch = useDispatch()
  const [mode, setMode] = useState(MODES.CALL_ID)
  const [callId, setCallId] = useState('')
  const [transcriptText, setTranscriptText] = useState('')
  const [side, setSide] = useState(SIDES.AGENT)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const handleClear = () => {
    setResult(null)
    setError(null)
  }

  const handleModeChange = (nextMode) => {
    if (!isLoading) {
      handleClear()
      setMode(nextMode)
    }
  }

  const isSubmitDisabled =
    isEmpty(prompt) || (mode === MODES.CALL_ID ? isEmpty(callId) : isEmpty(transcriptText))

  const getDisabledTooltip = () => {
    if (isEmpty(prompt)) {
      return 'Enter a prompt before testing'
    }
    if (mode === MODES.CALL_ID && isEmpty(callId)) {
      return 'Enter a call ID'
    }
    if (mode === MODES.RAW_TEXT && isEmpty(transcriptText)) {
      return 'Enter transcript text'
    }
    return null
  }

  const handleSubmit = async () => {
    handleClear()
    setIsLoading(true)

    const body =
      mode === MODES.CALL_ID
        ? { prompt, call_id: Number(callId) }
        : { prompt, transcript_text: transcriptText, side }

    try {
      const response = await dispatch(testPrompt(body))
      if (response) {
        setResult(response)
      } else {
        setError('Failed to test prompt')
      }
    } catch {
      setError('Failed to test prompt')
    } finally {
      setIsLoading(false)
    }
  }

  const explanations = result?.segments?.map((segment, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <p key={index}>{segment.explanation}</p>
  ))

  return (
    <div className="prompt-tester">
      <h4>Test Prompt</h4>
      <SegmentedControl
        items={[
          {
            accessor: MODES.CALL_ID,
            active: mode === MODES.CALL_ID,
            onClick: () => {
              handleModeChange(MODES.CALL_ID)
            },
            label: 'Call ID',
          },
          {
            accessor: MODES.RAW_TEXT,
            active: mode === MODES.RAW_TEXT,
            onClick: () => {
              handleModeChange(MODES.RAW_TEXT)
            },
            label: 'Raw Text',
          },
        ]}
      />

      {mode === MODES.CALL_ID ? (
        <div className="prompt-tester__inline">
          <TextInput
            placeholder="Enter call ID"
            value={callId}
            onChange={(event) => setCallId(event.target.value)}
            disabled={isLoading}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !isSubmitDisabled && !isLoading) {
                event.preventDefault()
                handleSubmit()
              }
            }}
          />
        </div>
      ) : (
        <>
          <div className="prompt-tester__inline">
            <Select
              options={SIDE_OPTIONS}
              value={side}
              onChange={(option) => setSide(option.value)}
              isClearable={false}
              isDisabled={isLoading}
            />
          </div>
          <TextArea
            placeholder="Enter transcript text"
            value={transcriptText}
            onChange={(event) => setTranscriptText(event.target.value)}
            rows={4}
            autoResize
            disabled={isLoading}
          />
        </>
      )}

      <div>
        <Button
          brand
          loading={isLoading}
          disabled={isSubmitDisabled}
          disabledTooltip={getDisabledTooltip()}
          onClick={handleSubmit}
        >
          Test Prompt
        </Button>
      </div>

      {error && (
        <Banner critical icon={<IconExclamationCircle />}>
          {error}
        </Banner>
      )}

      {result && (
        <>
          {result.overall_match ? (
            <Banner success icon={<IconCircleCheck />}>
              {explanations}
            </Banner>
          ) : (
            <Banner critical icon={<IconCircleX />}>
              {explanations}
            </Banner>
          )}
        </>
      )}
    </div>
  )
}
